import React from "react";
import Table from "@mui/material/Table";
import moment from "moment";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  TableHead,
  TableRow,
  Skeleton,
  Typography,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from "@mui/material";
import services from "../../services/services";
import styles from "./allSubmittedTable.module.scss";

export default function AllSubmittedFiles() {
  const [filesData, setFilesData] = useState(null);
  const [filteredFilesData, setFilteredFilesData] = useState(null);
  const [loader, setLoader] = useState(true);
  const [totalCount, setTotalCount] = useState(null);
  const [page, setPage] = useState(0);
  const [responseError, setResponseError] = useState({
    status: "",
    message: "",
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // NEW: Vendor filter state
  const [selectedVendor, setSelectedVendor] = useState("all");
  const [vendorList, setVendorList] = useState([]);
  
  useEffect(() => {
    fetchFileData(page, rowsPerPage);
  }, []);

  // NEW: Update filtered data when vendor selection changes
  useEffect(() => {
    if (filesData) {
      filterDataByVendor(selectedVendor);
    }
  }, [selectedVendor, filesData]);

  const handleDownload = (row) => {
    services.downloadFileReq(row.name, row.name).catch(err => {
      setResponseError(err)
    });
  };

  const fetchFileData = async (page, max_downloads) => {
    setLoader(true);
    const response = await services
      .getAllSubmittedFiles(page, max_downloads)  
      .then((response) => {
        if (response && response.data) {
          setFilesData(response.data);
          setFilteredFilesData(response.data);
          setTotalCount(response.total_count);
          
          // NEW: Extract unique vendors from the data
          extractVendors(response.data);
        }
        setLoader(false);
      })
      .catch((e) => {
        setLoader(false);
      });
  };

  // NEW: Extract unique vendors from files data
  const extractVendors = (data) => {
    const vendors = new Map();
    
    data.forEach(file => {
      if (file.user?.vendor) {
        const vendorKey = file.user.vendor_id;
        const vendorName = file.user.vendor.name;
        const vendorCountry = file.user.vendor.country;
        
        if (!vendors.has(vendorKey)) {
          vendors.set(vendorKey, {
            id: vendorKey,
            name: vendorName,
            country: vendorCountry,
            displayName: vendorCountry 
              ? `${vendorName} - ${vendorCountry}` 
              : vendorName
          });
        }
      }
    });
    
    // Convert Map to array and sort alphabetically
    const vendorArray = Array.from(vendors.values()).sort((a, b) => 
      a.displayName.localeCompare(b.displayName)
    );
    
    setVendorList(vendorArray);
  };

  // NEW: Filter data by selected vendor
  const filterDataByVendor = (vendorId) => {
    if (vendorId === "all") {
      setFilteredFilesData(filesData);
      setTotalCount(filesData?.length || 0);
    } else {
      const filtered = filesData.filter(file => 
        file.user?.vendor_id === vendorId
      );
      setFilteredFilesData(filtered);
      setTotalCount(filtered.length);
    }
    setPage(0); // Reset to first page when filtering
  };

  // NEW: Handle vendor filter change
  const handleVendorChange = (event) => {
    setSelectedVendor(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    console.log("newPage", newPage);
    setPage(newPage);
    // Note: If you want server-side filtering, you'll need to modify this
    // For now, this is client-side filtering
  };

  const clearError = () => {
    setResponseError(false);
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated data
  const paginatedData = filteredFilesData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Grid
      container
      display={"flex"}
      justifyContent={"center"}
      flexDirection={"column"}
    >
      {responseError.status && (
        <Snackbar
          sx={{mt: '5.25rem'}}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={Boolean(responseError)}
          onClose={clearError}
          autoHideDuration={5000}
        >
          <Alert severity="error">{responseError.message}</Alert>
        </Snackbar>
      )}

      <Grid item md={12} padding={2}>
        {/* NEW: Header with Title and Filter */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography color={"#3E4245"} variant="h6">
            All Submitted Files
          </Typography>
          
          {/* NEW: Vendor Filter Dropdown */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 250 }}>
            <InputLabel id="vendor-filter-label">Filter by Vendor</InputLabel>
            <Select
              labelId="vendor-filter-label"
              id="vendor-filter"
              value={selectedVendor}
              onChange={handleVendorChange}
              label="Filter by Vendor"
            >
              <MenuItem value="all">
                <em>All Vendors</em>
              </MenuItem>
              {vendorList.map((vendor) => (
                <MenuItem key={vendor.id} value={vendor.id}>
                  {vendor.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper} style={{ alignSelf: "center" }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead className={styles.headerclass}>
              <TableRow>
                <TableCell>
                  <Typography className={styles.headerText}>File Name</Typography>
                </TableCell>
                <TableCell>
                  <Typography className={styles.headerText}>User</Typography>
                </TableCell>
                <TableCell>
                  <Typography className={styles.headerText}>Vendor</Typography>
                </TableCell>
                <TableCell>
                  <Typography className={styles.headerText}>Org ID</Typography>
                </TableCell>
                <TableCell>
                  <Typography className={styles.headerText}>Time of Processing</Typography>
                </TableCell>
                <TableCell className={styles.headerText} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData && !(paginatedData.length) && (
                <TableRow>
                  <TableCell colSpan={6}> 
                    <Typography textAlign={"center"} color={"#3E4245"}>
                      {selectedVendor === "all" 
                        ? "No Submitted Files" 
                        : "No files found for selected vendor"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loader ? (
                paginatedData &&
                paginatedData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography className={styles.rowText}>
                        {row.name ? row.name : ""}
                      </Typography>
                    </TableCell>

                    <TableCell component="th" scope="row">
                      <Typography className={styles.rowText}>
                        {row.user?.first_name
                          ? `${row.user?.first_name} ${row.user?.last_name}`
                          : ""}
                      </Typography>
                    </TableCell>

                    <TableCell component="th" scope="row">
                      <Typography className={styles.rowText}>
                        {row.user?.vendor ? 
                          row.user?.country ? `${row.user?.vendor.name} - ${row.user?.vendor.country}` 
                                            : `${row.user?.vendor.name}`
                          : ""}
                      </Typography>
                    </TableCell>

                    <TableCell component="th" scope="row">
                      <Typography className={styles.rowText}>
                        {row.user?.vendor_id ? row.user?.vendor_id : ""}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography className={styles.rowText}>
                        {row.created_at
                          ? moment(row.created_at).format("DD-MM-YYYY hh:mm A")
                          : ""}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleDownload(row)}
                      >
                        <FileDownloadOutlinedIcon
                          color="secondary"
                          style={{ fontSize: "1.5rem" }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      style={{ height: "396px", width: "100%" }}
                      sx={{ bgcolor: "grey.150" }}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            count={totalCount || 0}
            page={page}
            labelDisplayedRows={({
              from,
              to,
              count,
            }) => `Showing Results of ${from}–${to} of ${
              totalCount !== -1 ? totalCount : `more than ${to}`
            }`}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </TableContainer>
      </Grid>
    </Grid>
  );
}
